import type { DateTime } from 'luxon'
import type {
  ContainerType,
  ExportBookingHold,
  ExportWatchState,
  GETExport,
  GETExportStatus,
  MatchedVesselForExport,
  ParsedExportEquipmentQuantity,
  PortComplex,
  SavedCustomer,
  ShippingLine,
  SimpleUser,
  TerminalName,
  VesselScheduleField,
  VesselScheduleForExport,
} from '~/services/apiClient'
import type { Appointment } from '~/models/appointments'
import type {
  VesselReceivingWindow,
  VesselScheduleStatus,
} from '~/models/vesselSchedule'
import { receivingWindowStatus } from '~/models/vesselSchedule'
import {
  parseScheduleChanges,
  scheduleChangeFor,
} from '~/models/scheduleDateChanges'
import { parseAppointments } from '~/stores/appointments'
import { AppointmentStatus } from '~/services/apiClient'
import { fastParseISO } from '~/utils'

// The generated client types many of these fields as non-null, but the backend
// (dd/models/exports.py) declares them `| None` — the checked-in client is
// stale on nullability, so we model them honestly as nullable here and
// normalize with `?? null`.

/** A hold is active when its status is HOLD; terminals also report RELEASED entries. */
export function isActiveHoldStatus(status: string): boolean {
  return status.toUpperCase() === 'HOLD'
}

/**
 * What to call a hold on screen.
 *
 * The short `reason_code` is the label and the longer `reason_description` is
 * the tooltip — the same split `getHoldDescription` makes for import holds,
 * where the badge carries the type and the tooltip carries the raw value and
 * source. Naming the hold is the point: a generic "HOLD" chip tells the user
 * something is wrong without telling them what.
 */
export function holdLabel(hold: ExportBookingHold): string {
  return hold.reason_code || hold.reason_description || 'Hold'
}

/**
 * The cutoff fields a container can actually be held to.
 *
 * A subset of `VesselScheduleField` so the labels come from
 * `VESSEL_SCHEDULE_FIELD_LABELS` rather than a second naming of the same
 * fields. `documentation_cutoff` is excluded on purpose: it is a paperwork
 * deadline for the shipper, not a gate deadline for a container, and no
 * design surfaces it on a row.
 */
export type ExportCutoffField = Extract<
  VesselScheduleField,
  'cargo_cutoff' | 'reefer_cutoff' | 'hazmat_cutoff'
>

/**
 * Every schedule date the Exports list and pane actually render.
 *
 * The cutoffs a row can be held to, plus the ERD — the two dates both surfaces
 * show. Named once because three things key off it: the cell's `field` prop,
 * the absence wording, and the recent-change lookup below.
 */
export type ExportScheduleDateField =
  ExportCutoffField | 'erd' | 'first_available_date'

/** A cutoff that applies to a row, and which field it came from. */
export interface ResolvedExportCutoff {
  field: ExportCutoffField
  at: DateTime
}

/** One schedule date that moved recently, and what it moved from. */
export interface ExportScheduleDateChange {
  /** The value this date held before its most recent move. */
  previous: DateTime
  /**
   * When this schedule's dates last moved.
   *
   * Schedule-level, not per field: the backend reports one timestamp for the
   * latest transition in which *any* ERD/cutoff moved, while each field's
   * previous value is taken at that field's own latest transition. So a date
   * that moved earlier in the window carries a timestamp later than its own
   * move, and the wording has to say "this schedule's dates last changed"
   * rather than claim it for the field.
   *
   * That vagueness is deliberate and cannot be tightened here: naming the
   * field ("ERD changed 2 hours ago") is false whenever a *different* date on
   * the same schedule moved more recently, which is the common case for a
   * terminal that reposts a whole voyage. Making it precise needs a per-field
   * timestamp on `VesselScheduleForExport`, i.e. backend work — not a reword.
   */
  changedAt: DateTime
}

/**
 * Schedule data for a booking's resolved vessel at one terminal.
 *
 * Built from `VesselScheduleForExport`, which the backend only includes when a
 * schedule row exists for that (vessel, terminal) pair — so the absence of an
 * entry means "no schedule for this terminal", distinct from
 * `ExportBooking.matchedVessel` being null ("vessel never resolved"). Both
 * render as an absent date, and the two must stay tellable apart: a bare dash
 * for either reads as broken data.
 */
export class ExportVesselSchedule {
  terminal: TerminalName
  voyageNumber: string | null
  eta: DateTime | undefined
  /** Earliest Receiving Date — date-only (no time/zone); serialized as "YYYY-MM-DD". */
  erd: DateTime | undefined
  cargoCutoff: DateTime | undefined
  documentationCutoff: DateTime | undefined
  reeferCutoff: DateTime | undefined
  hazmatCutoff: DateTime | undefined
  /** When this schedule was last scraped, for a staleness tooltip. */
  lastObserved: DateTime | undefined
  /**
   * When this schedule's ERD or a cutoff last moved, if that was inside the
   * backend's recent-change window (48h). Undefined means nothing moved
   * recently — the frontend applies no window of its own, or the two drift.
   */
  changedAt: DateTime | undefined
  /**
   * Each rendered date's value before its most recent move in that window.
   *
   * `documentation_cutoff` comes down the payload too and is deliberately
   * dropped: no surface shows that date, so a marker for it would flag a date
   * the user cannot see.
   *
   * Read it through `changeFor`, which pairs a previous value with the
   * timestamp — a previous value on its own says nothing about whether the
   * move was recent enough to report. Public rather than private only because
   * a private member is dropped when Vue unwraps an instance held in a store,
   * which makes the unwrapped type stop matching the class; `readonly` keeps
   * what is left of the guard.
   */
  readonly previousDates: Partial<Record<ExportScheduleDateField, DateTime>>

  constructor(data: VesselScheduleForExport) {
    this.terminal = data.terminal
    this.voyageNumber = data.voyage_number ?? null
    this.eta = data.eta ? fastParseISO(data.eta) : undefined
    this.erd = data.erd ? fastParseISO(data.erd) : undefined
    this.cargoCutoff = data.cargo_cutoff
      ? fastParseISO(data.cargo_cutoff)
      : undefined
    this.documentationCutoff = data.documentation_cutoff
      ? fastParseISO(data.documentation_cutoff)
      : undefined
    this.reeferCutoff = data.reefer_cutoff
      ? fastParseISO(data.reefer_cutoff)
      : undefined
    this.hazmatCutoff = data.hazmat_cutoff
      ? fastParseISO(data.hazmat_cutoff)
      : undefined
    this.lastObserved = data.last_observed
      ? fastParseISO(data.last_observed)
      : undefined
    const changes = parseScheduleChanges(data)
    this.changedAt = changes.changedAt
    this.previousDates = changes.previousDates
  }

  /** See `scheduleChangeFor`. */
  changeFor(
    field: ExportScheduleDateField
  ): ExportScheduleDateChange | undefined {
    return scheduleChangeFor(this, field)
  }

  /** Shared with the Schedule board — see `receivingWindowStatus`. */
  getStatus(now: DateTime): VesselScheduleStatus {
    return receivingWindowStatus(this satisfies VesselReceivingWindow, now)
  }

  /**
   * The cutoff a row for `line` is actually held to, or undefined when this
   * terminal posted none of the applicable ones.
   *
   * A line can carry several flags at once (a hazmat reefer really happens),
   * so this takes the **earliest** applicable cutoff rather than ranking the
   * flags: the row's real deadline is whichever comes first, and an arbitrary
   * precedence would show a later date than the one that actually closes the
   * gate on it. The winning field comes back too, so the cell can name which
   * cutoff it is showing instead of implying every date is the cargo cutoff.
   *
   * `is_over_dimensional` (the design's "Flat Rack") maps to the cargo cutoff:
   * terminals publish no separate over-dimensional cutoff and the API has no
   * such field, so those rows fall back to the general one like any other.
   */
  cutoffFor(
    line: ParsedExportEquipmentQuantity | undefined
  ): ResolvedExportCutoff | undefined {
    const applicable: ResolvedExportCutoff[] = []
    if (line?.is_hazmat && this.hazmatCutoff) {
      applicable.push({ field: 'hazmat_cutoff', at: this.hazmatCutoff })
    }
    if (line?.is_reefer && this.reeferCutoff) {
      applicable.push({ field: 'reefer_cutoff', at: this.reeferCutoff })
    }
    // The general cutoff always applies: a reefer whose terminal posted no
    // reefer cutoff is still held to the cargo one.
    if (this.cargoCutoff) {
      applicable.push({ field: 'cargo_cutoff', at: this.cargoCutoff })
    }
    return applicable.sort((a, b) => a.at.toMillis() - b.at.toMillis())[0]
  }
}

/** A per-terminal status snapshot for an export booking. */
export class ExportTerminalStatus {
  terminal: TerminalName
  shipping_line: string | null
  vessel: string | null
  voyage: string | null
  holds: ExportBookingHold[]
  equipment: ParsedExportEquipmentQuantity[]
  /**
   * Pick-empty quantity still available per container type. The backend
   * aggregates only equipment lines whose type parsed to our enum (unparseable
   * lines are dropped), so keys are always ContainerType enum values.
   *
   * A type is absent when the terminal hasn't given us a count for it yet —
   * read it through `availableToBookFor`, which keeps that apart from a zero.
   */
  available_to_book_by_type: { [key: string]: number }
  /**
   * The drop-full counterpart, carried for the M4 booking flow. Nothing reads
   * it yet — it is here so the model matches the payload rather than growing a
   * second shape later.
   */
  drop_available_by_type: { [key: string]: number }
  observed: DateTime

  constructor(data: GETExportStatus) {
    this.terminal = data.terminal
    this.shipping_line = data.shipping_line ?? null
    this.vessel = data.vessel ?? null
    this.voyage = data.voyage ?? null
    this.holds = data.holds
    this.equipment = data.equipment
    this.available_to_book_by_type = data.available_to_book_by_type
    this.drop_available_by_type = data.drop_available_by_type
    this.observed = fastParseISO(data.observed)
  }

  get activeHolds(): ExportBookingHold[] {
    return this.holds.filter((hold) => isActiveHoldStatus(hold.status))
  }

  /**
   * This terminal's equipment line for one container type, or undefined.
   *
   * The line is where the hazmat/reefer/over-dimensional flags live, and
   * `parsed_container_type` is the only thing that links one to a row —
   * `available_to_book_by_type` and `GETAppointment.container_type` are keyed
   * on the same parsed enum, so an allocation row and a booked row each
   * resolve to exactly one line (see rowEquipmentLine). A booked row recorded
   * before appointments carried a type has nothing to match on, and stays
   * blank rather than guessed.
   *
   * Undefined is a real answer, not an error — a terminal can report
   * availability for a type whose equipment line it didn't send.
   */
  equipmentFor(
    containerType: ContainerType
  ): ParsedExportEquipmentQuantity | undefined {
    return this.equipment.find(
      (line) => line.parsed_container_type === containerType
    )
  }

  /**
   * Every container type this terminal holds equipment for — the union of the
   * availability map's keys and the parsed equipment lines' own types.
   *
   * The union rather than either alone, because the two can disagree in both
   * directions. The backend drops a type from `available_to_book_by_type`
   * when its `pick_remaining` is null, and null is durable rather than a
   * post-deploy blip: a booking whose portal lookup stops succeeding keeps the
   * null indefinitely (org-level connection failures do exactly this). Keying
   * rows off the map alone makes those bookings lose every allocation row —
   * no count, no Book path, no cutoff warning. The other direction is already
   * documented on `equipmentFor`: a terminal can report availability for a
   * type whose equipment line it didn't send.
   */
  get containerTypes(): ContainerType[] {
    const types = new Set(
      Object.keys(this.available_to_book_by_type) as ContainerType[]
    )
    for (const line of this.equipment) {
      if (line.parsed_container_type) types.add(line.parsed_container_type)
    }
    return [...types]
  }

  /**
   * How many of one type are still available to book here, or null when the
   * terminal has given us no current count.
   *
   * Null and 0 are different answers and no caller may conflate them: 0 is the
   * terminal saying nothing is left, null is us not knowing. The backend keeps
   * a confirmed 0 in the map and omits only the unknown
   * (`dd/models/export_statuses.py`), so an absent key is precisely the
   * latter — and the booking API rejects an absent type just as it rejects a
   * zero, which is why neither can be offered as bookable.
   */
  availableToBookFor(containerType: ContainerType): number | null {
    return this.available_to_book_by_type[containerType] ?? null
  }

  /**
   * What is left to book here across every type, or null when the terminal
   * reported no count for any of them. Null rather than 0 for the same reason
   * `availableToBookFor` has it: a booking we have heard nothing about must
   * not be summarised as one with nothing left.
   */
  get totalAvailableToBook(): number | null {
    const counts = Object.values(this.available_to_book_by_type)
    if (!counts.length) return null
    return counts.reduce((sum, qty) => sum + qty, 0)
  }
}

/**
 * Everport prefixes its voyages with a numeric service code ("1472-018W")
 * where every other terminal, and the booking itself, sends "018W".
 */
function normalizeVoyage(
  voyage: string | null | undefined
): string | undefined {
  return voyage?.trim().toUpperCase().replace(/^\d+-/, '')
}

/** Missing voyage numbers aren't a disagreement — terminals often omit either one. */
export function voyagesAgree(
  bookingVoyage: string | null | undefined,
  scheduleVoyage: string | null | undefined
): boolean {
  const booking = normalizeVoyage(bookingVoyage)
  const schedule = normalizeVoyage(scheduleVoyage)
  return !booking || !schedule || booking === schedule
}

/**
 * Splits a booking's `vessel_schedules` into the ones that belong to its
 * voyage and the ones that don't.
 *
 * The vessel is matched by name, so a booking can resolve onto a schedule for
 * a different sailing of the same ship — those dates belong to a voyage this
 * booking is not on. Compared case- and whitespace-insensitively, and only
 * when both sides have a voyage: terminals routinely send one without the
 * other, and an absent voyage is not a disagreement.
 *
 * A module function rather than a private method: `ExportBooking` is matched
 * structurally in places (Vue unwraps reactive instances into plain objects),
 * and a `private` member makes those assignments fail to typecheck.
 */
function partitionSchedulesByVoyage(booking: {
  voyage: string | null
  vesselSchedules: ExportVesselSchedule[]
}): {
  matching: Map<TerminalName, ExportVesselSchedule>
  otherVoyage: Map<TerminalName, ExportVesselSchedule>
} {
  const matching = new Map<TerminalName, ExportVesselSchedule>()
  const otherVoyage = new Map<TerminalName, ExportVesselSchedule>()
  for (const schedule of booking.vesselSchedules) {
    const partition = voyagesAgree(booking.voyage, schedule.voyageNumber)
      ? matching
      : otherVoyage
    partition.set(schedule.terminal, schedule)
  }
  return { matching, otherVoyage }
}

/**
 * The appointment statuses that count as a move already covered.
 *
 * Not "anything but cancelled": a missed or aborted appointment leaves the
 * container still to be picked up, and a scheduling error never produced one
 * at all — counting those as done would tell a dispatcher they have nothing
 * left to book on a booking they still owe moves on. Completed counts, because
 * a pickup that already happened consumed one of the quantity.
 */
const COVERING_APPOINTMENT_STATUSES: ReadonlySet<AppointmentStatus> = new Set([
  AppointmentStatus.Scheduled,
  AppointmentStatus.InProgress,
  AppointmentStatus.Completed,
])

/**
 * One container type this org has declared how many of it is responsible for,
 * and how far along it is.
 *
 * Every figure here is **this organization's**, and none of them is comparable
 * to the terminal-wide TOTAL/AVAILABLE tallies the list already shows — those
 * count every carrier drawing on the release (DEV-8079). `terminalTotal` is
 * the one terminal-wide number in the struct, and it is carried only so a
 * declaration that has outgrown it can be flagged.
 */
export interface ExportResponsibility {
  containerType: ContainerType
  /**
   * What the user typed, and what every figure here counts against.
   *
   * **Never silently replaced by the terminal's total.** An earlier pass
   * displayed `min(declared, terminalTotal)` — DEV-8182's original
   * acceptance criterion — and in production that turned a declaration of 5
   * into a banner reading "0 of 1" with the only trace in a tooltip. It is
   * the one number on that banner the user put there; swapping it for a
   * terminal-derived one reads as the ratio pointing at the wrong field, not
   * as a correction. Entering more than the terminal holds is refused by the
   * dialog instead, and a stored value the terminal has since dropped below
   * is simply left as it was set.
   */
  declared: number
  /**
   * The terminal's own count for this type, or null when none was reported.
   *
   * Carried for the dialog, which validates a new quantity against it. The
   * list does not flag a stored value that has outgrown it: that can only
   * happen by drift (the release revised down after the fact, which the
   * backend deliberately permits), and the banner is already showing the
   * terminal's TOTAL a few inches away, so a marker would restate what is on
   * screen and add noise to a dense row.
   */
  terminalTotal: number | null
  /** Covered appointments of this type on the booking — ours, not the release's. */
  appointed: number
  /**
   * What is left to book against the declaration, counting only appointments
   * the terminal has given a size to. Never negative.
   *
   * Exact when `untiedAppointments` is 0, and an **upper bound** otherwise —
   * see the two fields below, which carry what is known about the gap.
   */
  stillNeeded: number
  /**
   * `unattributedAppointmentCount` for the whole booking, repeated on every
   * type's figure because that is the scope at which it is known: an untied
   * appointment could be any of the declared sizes, so it bears equally on
   * all of them and belongs to none.
   *
   * Non-zero means `stillNeeded` is overstated by somewhere between 0 and
   * this, and a surface showing it must say so. Zero is the normal
   * case at a terminal that types its appointments, and it is what the
   * backend fix restores everywhere.
   */
  untiedAppointments: number
  /**
   * True when the untied appointments are enough to cover every declared
   * size's remaining need at once, so each one can be read as met.
   *
   * The ordinary case is the whole of it: one declared size with one untied
   * appointment against it, where "which size is it" has only one answer
   *. Below that threshold the untied appointments are shared out
   * between sizes in some unknown way and nothing per-size can be concluded
   * — which is why this is all-or-nothing rather than a greedy allocation
   * that would pick a size arbitrarily and tell the user it is done.
   *
   * Still a guess, and the surface renders it as one: the appointments are
   * ours, on a booking this org declared, but their size is unconfirmed.
   */
  untiedCoversRemaining: boolean
}

/** A tracked export booking with its per-terminal statuses. */
export class ExportBooking {
  booking_number: string
  state: ExportWatchState
  port_complex: PortComplex | null
  related_terminals: TerminalName[]
  shipping_line: string | null
  parsed_shipping_line: ShippingLine | null
  vessel: string | null
  voyage: string | null
  added_by_user: SimpleUser | null
  customer: SavedCustomer | null
  /** Sorted by observed, most recent first */
  statuses: ExportTerminalStatus[]
  /**
   * Empty-pickup appointments drawing against this booking.
   *
   * Populated by `GET /exports/`, the detail endpoint and the `EXPORT_UPDATED`
   * websocket push alike, so a booking off the list already knows its
   * appointments and `[]` means it has none. The **watch/upload** endpoints are
   * the one exception — they still answer `include_appointments=False` to stay
   * lean, which is why `exportsStore.mergeExports` takes a caller-stated
   * `appointmentsIncluded` rather than trusting an empty array.
   */
  appointments: Appointment[]
  /**
   * The vessel this booking's free-text `vessel` name resolved to, or null
   * when it matched nothing we know.
   *
   * Non-null whenever the name resolves (aliases included) **even if no
   * schedule is on file for any of the booking's terminals** — that is what
   * makes "vessel matched, no schedule here" tellable apart from "vessel never
   * matched", so don't collapse the two.
   */
  matchedVessel: MatchedVesselForExport | null
  /** Per-terminal schedules for `matchedVessel`; see `scheduleByTerminal`. */
  vesselSchedules: ExportVesselSchedule[]
  /**
   * The containers the booking covers, as the backend counts them — the same
   * figure the Add bookings table shows before the booking is tracked. Null
   * when no terminal has reported equipment, and on a payload that predates
   * the field (see `totalQty`, its only reader).
   */
  totalContainers: number | null
  /**
   * How many of each container type **this organization** is responsible for,
   * as somebody here typed it (DEV-8140). Per org, because the watch is.
   *
   * Read it through `responsibilityFor` / `declaredResponsibilities`, which
   * apply the cap and pair it with what has been appointed. An absent key is
   * "not declared" and is not a zero — a zero is a user saying they owe none
   * of that type, which the backend accepts and stores.
   *
   * Keyed on the same `ContainerType` enum as `available_to_book_by_type`,
   * `equipmentFor` and an appointment's `container_type`, so a declaration
   * joins to a terminal's line and to our appointments without a second
   * mapping. The generated client types the map's keys as plain strings; it is
   * narrowed here because every other type-keyed map on this model is.
   */
  carrierQuantities: Partial<Record<ContainerType, number>>

  constructor(data: GETExport, demoMode: boolean) {
    this.booking_number = data.booking_number
    this.state = data.state
    this.port_complex = data.port_complex ?? null
    this.related_terminals = data.related_terminals
    this.shipping_line = data.shipping_line ?? null
    this.parsed_shipping_line = data.parsed_shipping_line ?? null
    this.vessel = data.vessel ?? null
    this.voyage = data.voyage ?? null
    this.added_by_user = data.added_by_user ?? null
    this.customer = data.customer ?? null
    this.statuses = data.statuses
      .map((status) => new ExportTerminalStatus(status))
      .sort((a, b) => b.observed.toMillis() - a.observed.toMillis())
    this.appointments = parseAppointments(data.appointments ?? [], demoMode)
    this.matchedVessel = data.matched_vessel ?? null
    this.vesselSchedules = (data.vessel_schedules ?? []).map(
      (schedule) => new ExportVesselSchedule(schedule)
    )
    this.totalContainers = data.total_containers ?? null
    // The backend answers `{}` rather than null for "never set", so the `??`
    // only covers a payload from before the field shipped (a websocket push
    // held in flight across the deploy, and every fixture that predates it).
    this.carrierQuantities = (data.carrier_quantities ?? {}) as Partial<
      Record<ContainerType, number>
    >
  }

  /**
   * Schedules keyed by terminal, for a row to look up its own terminal's
   * dates.
   *
   * **Keyed by the `terminal` field, never positionally.** `statuses` can hold
   * several rows per terminal (different sources) while `vessel_schedules`
   * holds at most one, so the two arrays' lengths differ by design and index
   * `n` of one is not index `n` of the other.
   *
   * A schedule whose voyage disagrees with the booking's is dropped: the
   * vessel is matched by name, so a booking can resolve onto a schedule for a
   * different sailing of the same ship, and those dates belong to a voyage
   * this booking is not on. Compared case- and whitespace-insensitively, and
   * only when both sides have a voyage — terminals routinely send one without
   * the other, and an absent voyage is not a disagreement.
   */
  get scheduleByTerminal(): Map<TerminalName, ExportVesselSchedule> {
    return partitionSchedulesByVoyage(this).matching
  }

  /**
   * Schedules dropped by the voyage filter, keyed by terminal.
   *
   * Exists so an absent date can say which it is. "We have no schedule for
   * this terminal" and "we have one, but for another sailing of this ship"
   * are different facts, and reporting the first for the second tells the
   * user something untrue.
   */
  get schedulesForOtherVoyages(): Map<TerminalName, ExportVesselSchedule> {
    return partitionSchedulesByVoyage(this).otherVoyage
  }

  /**
   * The booking banner's ERD: the earliest across its terminals (EXP-001,
   * "earliest ERD of all containers within the booking"). Undefined when no
   * terminal has a schedule with an ERD.
   *
   * Scoped to terminals the booking actually has a status for, the same as
   * `distinctCutoffs` — a schedule for a terminal holding none of this
   * booking's containers is not part of its window. The payload does not
   * currently carry one (the backend scopes `vessel_schedules` to the status
   * terminals), but the two getters disagreeing about it is how that stops
   * being true quietly.
   */
  get earliestErd(): DateTime | undefined {
    const schedules = this.scheduleByTerminal
    return [...this.latestStatusByTerminal.keys()]
      .map((terminal) => schedules.get(terminal)?.erd)
      .filter((erd): erd is DateTime => erd !== undefined)
      .sort((a, b) => a.toMillis() - b.toMillis())[0]
  }

  /**
   * Every distinct deadline the booking's containers are held to, earliest
   * first.
   *
   * A booking spanning terminals, or mixing reefer/hazmat with general cargo,
   * genuinely has several. Keyed on the resolved value rather than the
   * terminal: two terminals posting the same cargo cutoff is one deadline to
   * a user, not two — so the banner's `+N` counts real alternatives.
   *
   * The single walk behind both this and `earliestCutoff`. They were two
   * copies, which meant the banner's date and its `+N` could drift apart.
   */
  get distinctCutoffs(): ResolvedExportCutoff[] {
    const schedules = this.scheduleByTerminal
    const byValue = new Map<string, ResolvedExportCutoff>()
    for (const status of this.latestStatusByTerminal.values()) {
      const schedule = schedules.get(status.terminal)
      if (!schedule) continue
      // A terminal with availability but no equipment lines still has a cargo
      // cutoff worth showing, so fall back to resolving against no line.
      const lines = status.equipment.length ? status.equipment : [undefined]
      for (const line of lines) {
        const cutoff = schedule.cutoffFor(line)
        if (cutoff) {
          byValue.set(`${cutoff.field}@${cutoff.at.toMillis()}`, cutoff)
        }
      }
    }
    return [...byValue.values()].sort(
      (a, b) => a.at.toMillis() - b.at.toMillis()
    )
  }

  /**
   * The earliest deadline any of this booking's containers is held to
   * (EXP-001, "earliest cutoff date of all containers within booking").
   *
   * Resolved per equipment line rather than just the earliest cargo cutoff,
   * so a booking whose only urgent container is a reefer shows that reefer
   * deadline — the banner has to agree with the soonest date visible on the
   * rows beneath it, or it reads as a bug.
   */
  get earliestCutoff(): ResolvedExportCutoff | undefined {
    return this.distinctCutoffs[0]
  }

  get latestStatusByTerminal(): Map<TerminalName, ExportTerminalStatus> {
    const byTerminal = new Map<TerminalName, ExportTerminalStatus>()
    // statuses are sorted most-recent-first, so the first entry per terminal wins
    for (const status of this.statuses) {
      if (!byTerminal.has(status.terminal)) {
        byTerminal.set(status.terminal, status)
      }
    }
    return byTerminal
  }

  /**
   * Appointments grouped by terminal, so a per-terminal surface (the details
   * drawer today) can render its own alongside that terminal's status. Only
   * terminals that actually have appointments are keys.
   */
  get appointmentsByTerminal(): Map<TerminalName, Appointment[]> {
    const byTerminal = new Map<TerminalName, Appointment[]>()
    for (const appointment of this.appointments) {
      const existing = byTerminal.get(appointment.terminal)
      if (existing) {
        existing.push(appointment)
      } else {
        byTerminal.set(appointment.terminal, [appointment])
      }
    }
    return byTerminal
  }

  get latestObserved(): DateTime | undefined {
    return this.statuses[0]?.observed
  }

  /** Active holds across the latest status of each terminal */
  get activeHolds(): ExportBookingHold[] {
    return [...this.latestStatusByTerminal.values()].flatMap(
      (status) => status.activeHolds
    )
  }

  get hasHolds(): boolean {
    return this.activeHolds.length > 0
  }

  /**
   * The line code (COSU), or the terminal's raw text when the line never
   * parsed — all we know for those bookings. The filter deliberately excludes
   * the unparsed ones, so the raw text is a hint, not a match.
   */
  get shippingLineCode(): string | null {
    return this.parsed_shipping_line ?? this.shipping_line
  }

  /**
   * "VESSEL/VOYAGE", or just the vessel, or null — never a placeholder.
   *
   * Terminals report the two independently and often send one without the
   * other, so rendering `${vessel ?? '—'}/${voyage ?? '—'}` produced strings
   * like "—/0" that read as broken data rather than as absent data. Callers
   * omit the whole fragment when this is null.
   *
   * A voyage with no vessel is still shown ("Voyage 110W"): eModal names no
   * vessel for most bookings (the backend names it from the IMO when it can),
   * and the voyage is the sailing detail the user checks against eModal. An
   * all-zero voyage is a placeholder (Pier A sends "0"), so it is dropped.
   */
  get vesselVoyageLabel(): string | null {
    const vessel = this.vessel?.trim()
    const voyage = this.voyage?.trim()
    if (!vessel) {
      return voyage && !/^0+$/.test(voyage) ? `Voyage ${voyage}` : null
    }
    return voyage ? `${vessel}/${voyage}` : vessel
  }

  /**
   * The booking's own total, or null when not one of its terminals reported a
   * count. A booking with counts at one terminal and none at another sums what
   * is known: the tally is a summary, and the rows below carry each terminal's
   * own answer — but a total of nothing known has to say so, or the banner
   * ends up asserting "0 AVAILABLE" about a release nobody has looked at.
   */
  get totalAvailableToBook(): number | null {
    const known = [...this.latestStatusByTerminal.values()]
      .map((status) => status.totalAvailableToBook)
      .filter((total) => total !== null)
    if (!known.length) return null
    return known.reduce((sum, total) => sum + total, 0)
  }

  /** Equipment lines from the latest status of each terminal, flattened */
  get equipmentSummary(): ParsedExportEquipmentQuantity[] {
    return [...this.latestStatusByTerminal.values()].flatMap(
      (status) => status.equipment
    )
  }

  /**
   * The booking's total at the terminal, summed across `equipmentSummary` — the
   * banner's counterpart to `totalAvailableToBook`, labelled TOTAL there. Goes
   * through the equipment lines rather than the availability map because that's
   * the only place `booked_qty` lives.
   *
   * Not "booked", though `booked_qty` is the field it reads: the terminal's
   * `bookedctr_qty` is the fixed number of appointments the release allows per
   * size/type and does not move as appointments are made (DEV-8079). So this
   * is not the other half of a remainder — `total − available` recovers
   * nothing. `401610383382` reports a total of 10, 2 already issued and still
   * 10 available; `DALE55034000` at OICT reads a total of 1 and 0 available with
   * nothing ever issued, carrying an EGF hold. What has actually been picked
   * up is `issued_qty`, which is TOS-sourced, display-only and per the
   * terminal not always accurate. Booking surfaces must not subtract the two,
   * which is why this is a booking-level total only — the pair sat in adjacent
   * columns on a row until DEV-8079 and invited exactly that.
   *
   * `GETExport.total_containers` when the backend sent it, which is also what
   * the Add bookings table shows before the booking is tracked — so the dialog
   * and the banner cannot disagree about one booking.
   *
   * Null when no terminal has reported equipment — the same rule
   * `totalAvailableToBook` follows, and for the same reason. A booking whose
   * lookups have never landed has no data on either figure, so reporting a
   * confirmed `0` beside an `Unknown` gave two different answers about one
   * absence.
   */
  get totalQty(): number | null {
    if (this.totalContainers !== null) return this.totalContainers
    // Fallback for a payload without the field (one held in flight across the
    // deploy that added it, and older fixtures). Same rule the backend
    // applies, so the two cannot disagree: per type the largest terminal's
    // figure, summed across types. Flat-summing the lines instead
    // double-counted every booking listed at two terminals — 4 of the 219 in
    // the last 30 days of production, all 4 reporting an identical quantity at
    // both terminals rather than two separate allocations.
    //
    // Keyed on the terminal's own `container_type_code`, not the parsed type:
    // a line whose code we have not mapped yet still counts, and dropping it
    // would under-report instead of over-reporting.
    const largestByTypeCode = new Map<string, number>()
    for (const line of this.equipmentSummary) {
      const quantity = line.booked_qty ?? 0
      const current = largestByTypeCode.get(line.container_type_code)
      if (current === undefined || quantity > current) {
        largestByTypeCode.set(line.container_type_code, quantity)
      }
    }
    if (!largestByTypeCode.size) return null
    return [...largestByTypeCode.values()].reduce(
      (sum, quantity) => sum + quantity,
      0
    )
  }

  /**
   * The terminal's total for one container type, or null when no terminal
   * reported a line for it.
   *
   * The **largest** terminal's figure, not the sum: terminals repeat a
   * booking's lines rather than splitting them, so adding them up invents
   * containers on the three bookings that span terminals. That is the rule the
   * backend states for `total_containers` ("per type the largest terminal
   * count wins"), applied here per type. `totalQty` applies the same rule
   * across every type, so the two agree.
   */
  /**
   * Every container type the booking can carry a declaration for, in the
   * order its terminals reported them.
   *
   * The union across terminals (a type at two terminals is one type, keyed by
   * type alone like the declaration itself) **plus any type already declared**
   * — a terminal can stop reporting a line the org has a quantity against,
   * and dropping it here would leave that quantity on the booking with no way
   * to see or clear it.
   */
  get containerTypes(): ContainerType[] {
    const types = new Set<ContainerType>()
    for (const status of this.latestStatusByTerminal.values()) {
      for (const containerType of status.containerTypes)
        types.add(containerType)
    }
    for (const containerType of Object.keys(
      this.carrierQuantities
    ) as ContainerType[]) {
      types.add(containerType)
    }
    return [...types]
  }

  bookedQtyFor(containerType: ContainerType): number | null {
    const totals = [...this.latestStatusByTerminal.values()]
      .map((status) => status.equipmentFor(containerType)?.booked_qty)
      .filter((qty): qty is number => qty !== undefined)
    if (!totals.length) return null
    return Math.max(...totals)
  }

  /**
   * How many of one container type are still bookable across the booking's
   * terminals, or null when none of them reported a count for it.
   *
   * Summed, unlike `bookedQtyFor`, which takes the largest: terminals repeat
   * a booking's *ordered* lines, but each reports its own remaining pool, and
   * two terminals each with one left really is two left. The same rule
   * `totalAvailableToBook` already applies to the whole booking, narrowed to
   * one type.
   *
   * Context only — never a ceiling for a declaration. It moves as anyone
   * books or gates out, our own appointments included, so a quantity
   * validated against it would start rejecting the number it had itself
   * accepted (see the dialog).
   */
  availableToBookFor(containerType: ContainerType): number | null {
    const known = [...this.latestStatusByTerminal.values()]
      .map((status) => status.availableToBookFor(containerType))
      .filter((qty): qty is number => qty !== null)
    if (!known.length) return null
    return known.reduce((sum, qty) => sum + qty, 0)
  }

  /**
   * How many of one container type this org already has an appointment for.
   *
   * Ours alone — appointments are only ever the ones made through us — which
   * is what makes this countable against a declared quantity and meaningless
   * against the terminal's tallies. Across every terminal on the booking,
   * matching how the declaration is keyed: by type only.
   */
  appointedFor(containerType: ContainerType): number {
    return this.appointments.filter(
      (appointment) =>
        appointment.container_type === containerType &&
        COVERING_APPOINTMENT_STATUSES.has(appointment.status)
    ).length
  }

  /**
   * Covered appointments the terminal has assigned no container type to.
   *
   * **Whether this happens is a property of the terminal, not of the
   * appointment.** Measured across production on 2026-09-30: PCT reports no
   * type on any of them (3 of 3, on all 3 of its bookings) while OICT (147)
   * and Everport Oakland (19) always do. So a rule that can only count typed
   * appointments does not degrade gracefully at PCT — it reads 0 there
   * forever, however many appointments the booking has. That is why
   * `responsibilityTotals` counts these and the per-type figures cannot.
   *
   * Every one of them in production was in fact booked *through us*, with the
   * size chosen explicitly and recorded on `empty_pickup_booking_requests` —
   * the backend just never stamps it onto the appointment. When that is fixed
   * these mostly disappear, but not entirely: an appointment made directly in
   * the terminal's own portal still arrives typeless.
   */
  get unattributedAppointmentCount(): number {
    return this.appointments.filter(
      (appointment) =>
        appointment.container_type === null &&
        COVERING_APPOINTMENT_STATUSES.has(appointment.status)
    ).length
  }

  /**
   * This org's declaration for one container type, or undefined when it has
   * declared none for that type.
   *
   * Undefined is the whole point of the feature's fallback rule: a type with
   * no declaration has no ratio and no "still needed", and must not borrow the
   * terminal's total to invent one (DEV-8182 AC4) — that would put a count of
   * our appointments over a figure covering every carrier on the release.
   */
  responsibilityFor(
    containerType: ContainerType
  ): ExportResponsibility | undefined {
    const declared = this.carrierQuantities[containerType]
    if (declared === undefined) return undefined
    const terminalTotal = this.bookedQtyFor(containerType)
    const appointed = this.appointedFor(containerType)
    const untiedAppointments = this.unattributedAppointmentCount
    return {
      containerType,
      declared,
      terminalTotal,
      appointed,
      stillNeeded: Math.max(0, declared - appointed),
      untiedAppointments,
      untiedCoversRemaining: this.untiedCoversRemaining,
    }
  }

  /**
   * Whether the untied appointments are enough to settle every declared
   * size's remaining need at once — a property of the **booking**, which is
   * why it lives here and every type's figure copies it.
   *
   * The banner used to answer the same question its own way, from the
   * aggregate ratio (`appointed >= responsible`). Those two disagree as soon
   * as one size is over-appointed, because the aggregate nets the surplus
   * off another size's shortfall while `remainingTypedNeed` floors each size
   * at zero. Declared 1 / appointed 3 beside declared 2 / appointed 0, with
   * one untied: the aggregate reads 4 of 3 and calls it covered while the
   * second row is still asking for two containers. That is the banner
   * contradicting a row on the same booking — this feature's whole subject —
   * so there is exactly one answer and both surfaces read it.
   */
  get untiedCoversRemaining(): boolean {
    const untied = this.unattributedAppointmentCount
    return untied > 0 && untied >= this.remainingTypedNeed
  }

  /**
   * What every declared size still needs, added up, counting only typed
   * appointments.
   *
   * Keyed by size and so already deduplicated across terminals, which is the
   * reason it is summed from `carrierQuantities` rather than from the rows:
   * a size the booking holds at two terminals renders its figure on both
   * rows (see `ROW_STILL_NEEDED_TOOLTIP`) but is one need, and counting it
   * twice here would set the bar for "the untied ones cover it" at double
   * what the org actually owes.
   *
   * Not built on `declaredResponsibilities`, which would recurse back
   * through `responsibilityFor`.
   *
   * Public only because `private` makes the class nominally typed, and these
   * models are read back out of a Pinia store through a reactive proxy that
   * structural typing has to accept. Read `responsibilityFor` instead.
   */
  get remainingTypedNeed(): number {
    return (Object.keys(this.carrierQuantities) as ContainerType[]).reduce(
      (sum, containerType) =>
        sum +
        Math.max(
          0,
          (this.carrierQuantities[containerType] ?? 0) -
            this.appointedFor(containerType)
        ),
      0
    )
  }

  /** Every type this org has declared a quantity for. Empty when none has. */
  get declaredResponsibilities(): ExportResponsibility[] {
    return (Object.keys(this.carrierQuantities) as ContainerType[])
      .map((containerType) => this.responsibilityFor(containerType))
      .filter((r): r is ExportResponsibility => r !== undefined)
  }

  /**
   * The booking-level "N of M appointed", or undefined when this org has
   * declared nothing on it.
   *
   * M is summed over the **declared types only**, exactly as typed. A booking
   * with one type declared and another left alone (the real `RICGG2136300`
   * case: 40FH set, 40FR not) reports only the declared one rather than
   * topping M up with the terminal's figure for the rest — mixing a number the
   * user typed with a terminal-wide fallback is the cross-population
   * arithmetic DEV-8079 removed, wearing a different label.
   *
   * N counts an appointment when its type is one of the declared ones **or
   * when the terminal has assigned it no type at all**. The second half is not
   * rounding up: at PCT no export pickup carries a type at all (see
   * `unattributedAppointmentCount`), so a typed-only N reads 0 on every PCT
   * booking forever. An appointment against a booking this org has declared
   * itself responsible for is one of theirs; the only open question is which
   * size it will turn out to be, and that question belongs to the per-type
   * figures, which leave it out.
   *
   * An appointment of a type the org did *not* declare stays out either way —
   * it is outside the population M measures.
   *
   * N is not clamped to M. More appointed than declared is a real state, so
   * "3 of 2" is the honest reading of one; clamping would make a declaration
   * that needs revisiting look settled.
   */
  get responsibilityTotals():
    { responsible: number; appointed: number } | undefined {
    const declared = this.declaredResponsibilities
    if (!declared.length) return undefined
    return {
      responsible: declared.reduce((sum, r) => sum + r.declared, 0),
      appointed:
        declared.reduce((sum, r) => sum + r.appointed, 0) +
        this.unattributedAppointmentCount,
    }
  }
}

export function parseExports(
  data: GETExport[],
  demoMode: boolean
): ExportBooking[] {
  return data.map((d) => new ExportBooking(d, demoMode))
}
